<template>
  <div class="panel-content">
    <RowElements
      v-for="(column, index) in visibleRows"
      :key="`row-${index}`"
      :items="column"
      :data-testid="resolveRowTestId(column, index)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import RowElements from './RowElements.vue'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'
import { buildStableTestId } from '@/common/utils/testId'
import { ElementContextKey } from '@/components/platform/injection-keys'

interface Props {
  rows: any[]
}

const props = defineProps<Props>()
const INITIAL_PROGRESSIVE_ROWS = 8
const PROGRESSIVE_ROW_CHUNK = 4
const PROGRESSIVE_ROW_DELAY_MS = 16
const PROGRESSIVE_MIN_ROWS = 6
const PROGRESSIVE_MIN_ELEMENTS = 8
let progressiveTimer: ReturnType<typeof setTimeout> | null = null

const filteredRows = computed(() =>
  props.rows.filter(row =>
    row?.element?.type?._text !== 'LineDelimiter'
  )
)

const elementCount = computed(() =>
  filteredRows.value.reduce((count, row) => {
    if (!Array.isArray(row)) return count
    return count + row.filter((cell: any) => cell?.element).length
  }, 0),
)

const shouldProgressivelyMount = computed(() =>
  filteredRows.value.length > PROGRESSIVE_MIN_ROWS &&
  elementCount.value > PROGRESSIVE_MIN_ELEMENTS,
)

const visibleRowCount = ref(0)

const getInitialVisibleRowCount = () =>
  shouldProgressivelyMount.value
    ? Math.min(INITIAL_PROGRESSIVE_ROWS, filteredRows.value.length)
    : filteredRows.value.length

const visibleRows = computed(() => filteredRows.value.slice(0, visibleRowCount.value))

const elementContext = inject(ElementContextKey)
const formPath = computed(() => ({
  panelId: elementContext?.panelId,
  formId: elementContext?.formId,
}))

const resolveRowTestId = (row: any[], rowIndex: number) => {
  return buildStableTestId(`elementsRow-${rowIndex}`, [
    formPath.value.formId,
    formPath.value.panelId,
  ])
}

const clearProgressiveTimer = () => {
  if (progressiveTimer === null) return
  clearTimeout(progressiveTimer)
  progressiveTimer = null
}

const scheduleNextRowChunk = () => {
  clearProgressiveTimer()
  if (!shouldProgressivelyMount.value) {
    visibleRowCount.value = filteredRows.value.length
    return
  }
  if (visibleRowCount.value >= filteredRows.value.length) return

  progressiveTimer = setTimeout(() => {
    visibleRowCount.value = Math.min(
      visibleRowCount.value + PROGRESSIVE_ROW_CHUNK,
      filteredRows.value.length,
    )
    startWorkflowMeasure('PanelContent.progressiveRows', {
      renderedRows: visibleRowCount.value,
      totalRows: filteredRows.value.length,
      elementCount: elementCount.value,
    })()
    scheduleNextRowChunk()
  }, PROGRESSIVE_ROW_DELAY_MS)
}

watch(
  [filteredRows, shouldProgressivelyMount],
  () => {
    clearProgressiveTimer()
    visibleRowCount.value = getInitialVisibleRowCount()
  },
  { immediate: true },
)

onMounted(() => {
  startWorkflowMeasure('PanelContent.onMounted', {
    rowCount: visibleRows.value.length,
    totalRowCount: filteredRows.value.length,
    elementCount: elementCount.value,
    progressive: shouldProgressivelyMount.value,
  })()
  scheduleNextRowChunk()
})

onUnmounted(() => {
  clearProgressiveTimer()
})
</script>

<style scoped>
.panel-content {
  width: 100%;
  min-height: 0;
}
</style>
